'use client'

import { useState, useEffect } from 'react'
import type { ShowcaseProject } from '@/types'
import { resolveHref, urlForImage } from '@/sanity/lib/utils'
import { createDataAttribute } from 'next-sanity'
import { studioUrl } from '@/sanity/lib/api'
import Link from 'next/link'

interface ShowcaseSlideshowProps {
  projects: ShowcaseProject[]
  dataId?: string
  dataType?: string
  autoPlayInterval?: number
}

export function ShowcaseSlideshow({
  projects,
  dataId,
  dataType,
  autoPlayInterval = 5000,
}: ShowcaseSlideshowProps) {
  const [currentSlide, setCurrentSlide] = useState(0)

  const dataAttribute =
    dataId && dataType
      ? createDataAttribute({
          baseUrl: studioUrl,
          id: dataId,
          type: dataType,
        })
      : null

  // Auto-advance slides
  useEffect(() => {
    if (projects.length <= 1) return

    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % projects.length)
    }, autoPlayInterval)

    return () => clearInterval(interval)
  }, [currentSlide, projects.length, autoPlayInterval])

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
  }

  if (!projects || projects.length === 0) {
    return null
  }

  return (
    <div className="relative mx-auto max-w-[100rem] overflow-hidden rounded-md shadow-2xl">
      {/* Slides Container */}
      <div className="relative h-[400px] md:h-[500px] lg:h-[600px]">
        {projects.map((project, index) => {
          const href = resolveHref(project?._type, project?.slug)
          if (!href) return null
          const imageUrl = project.coverImage && urlForImage(project.coverImage)?.fit('crop').url()

          return (
            <div
              key={project._key}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}>
              <Link
                className="block h-full transition-colors hover:bg-gray-50/50"
                href={href}
                data-sanity={dataAttribute?.(['showcaseProjects', { _key: project._key }])}>
                <div className="z-1 absolute left-4 top-4 max-w-[50%] rounded bg-white/80 px-3 py-1.5 text-sm text-gray-600 backdrop-blur-sm md:left-6 md:top-6 md:text-base">
                  <h1 className="text-lg font-bold text-gray-600">{project.title}</h1>
                  {/* <CustomPortableText
                    id={project._id}
                    type={project._type}
                    path={['overview']}
                    paragraphClasses="text-gray-600 line-clamp-1 md:text-base text-sm"
                    value={project.overview as PortableTextBlock[]}
                  /> */}
                </div>
                <div
                  className="h-full w-full bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${imageUrl})`,
                  }}></div>
              </Link>
            </div>
          )
        })}
      </div>

      {/* Navigation Dots */}
      <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 transform space-x-2">
        {projects.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-3 w-3 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'bg-white shadow-lg' : 'bg-white/60 hover:bg-white/80'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  )
}
